import { Button, useDisclosure } from '@ds';
import { ConditionalRender } from '@ui';
import { CaseStatus, ClinicalData, ClinicalItemType, OutcomeType, OutcomeData, Randomization } from '@/api/apis/case';
import { getClinicalRecordByType, isAllowedStatus, isRandomizationScs } from '@/utils/helpers';
import { outcomeTypeNames } from '@/constants/case/case.constants';
import { getOutcomeLabel } from '../../../CaseCard/utils';
import SectionRow from '../SectionRow';
import { getDiscardDescription } from './utils';
import { OutcomeModal } from './OutcomeModal';

type ServiceOutcomeModalProps = {
  outcomeData?: OutcomeData;
  clinicalData: ClinicalData;
  status: CaseStatus;
  randomization?: Randomization;
};

const btnClassNames = 'h-auto text-m-m! py-1 px-0 justify-start underline data-[hover=true]:bg-transparent';

export const ServiceOutcome = ({ status, outcomeData, clinicalData, randomization }: ServiceOutcomeModalProps) => {
  const {
    isOpen: isOpenServiceDecline,
    onOpen: onOpenServiceDecline,
    onClose: onCloseServiceDecline,
  } = useDisclosure();
  const {
    isOpen: isOpenOneLungTurndown,
    onOpen: onOpenOneLungTurndown,
    onClose: onCloseOneLungTurndown,
  } = useDisclosure();
  const isScsCase = isRandomizationScs(randomization);
  const discardConfirmationFormData = getClinicalRecordByType(clinicalData, ClinicalItemType.DiscardConfirmationForm);
  const oneLungTurndown = getClinicalRecordByType(clinicalData, ClinicalItemType.OneLungTurndown);
  const turndownOutcome = oneLungTurndown ? oneLungTurndown.item?.turndownOutcome : outcomeData?.turndownOutcome;
  const discardDescription = getDiscardDescription(discardConfirmationFormData, turndownOutcome, oneLungTurndown);
  const serviceCompletedByStatus = isAllowedStatus({
    statusFrom: CaseStatus.OrganOffOCS,
    currentStatus: status,
  });
  const serviceCompleted =
    serviceCompletedByStatus &&
    status !== CaseStatus.ServiceDeclined &&
    outcomeData?.outcomeType === OutcomeType.Transplant &&
    !isScsCase;
  const hasDeclineOutcome = !!outcomeData && outcomeData.outcomeType !== OutcomeType.Transplant;

  return (
    <ConditionalRender show={serviceCompleted || hasDeclineOutcome || !!oneLungTurndown}>
      <SectionRow truncate={false} label="Service Outcome">
        <div className="flex gap-x-1 items-center flex-wrap">
          {serviceCompleted && <div className="text-m-m">Service Completed</div>}
          {hasDeclineOutcome && (
            <Button className={btnClassNames} variant="light" size="md" disableRipple onClick={onOpenServiceDecline}>
              {outcomeTypeNames[outcomeData.outcomeType]}
            </Button>
          )}
          {oneLungTurndown && (
            <Button className={btnClassNames} variant="light" size="md" disableRipple onClick={onOpenOneLungTurndown}>
              {getOutcomeLabel(oneLungTurndown, outcomeData)}
            </Button>
          )}
        </div>
      </SectionRow>
      <OutcomeModal
        header="One Lung Turndown"
        oneLungTurndown={oneLungTurndown}
        discardConfirmationFormData={discardConfirmationFormData}
        discardDescription={discardDescription}
        isOpen={isOpenOneLungTurndown}
        onOpen={onOpenOneLungTurndown}
        onClose={onCloseOneLungTurndown}
      />
      <OutcomeModal
        header="Service Outcome"
        outcomeData={outcomeData}
        discardConfirmationFormData={discardConfirmationFormData}
        discardDescription={discardDescription}
        isOpen={isOpenServiceDecline}
        onOpen={onOpenServiceDecline}
        onClose={onCloseServiceDecline}
      />
    </ConditionalRender>
  );
};
